import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { getDevice } from '../services/devices';
import { getMyRank } from '../services/userDevices';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { ProductCard } from '../components/ui/ProductCard';
import { StatusPill } from '../components/ui/StatusPill';
import { TrackDeviceModal } from '../components/ui/TrackDeviceModal';
import { useAuth } from '../hooks/useAuth';
import { useMyDevices } from '../hooks/useMyDevices';
import { estimateBatteryLife } from '../lib/batteryLife';
import type { UserDevice } from '../types/models';

type Tab = 'devices' | 'notifications' | 'profile';

function MyDeviceCard({ ud }: { ud: UserDevice }) {
  const deviceQ = useQuery({
    queryKey: ['device', ud.deviceId],
    queryFn: () => getDevice(ud.deviceId),
  });

  if (deviceQ.isLoading) {
    return (
      <div className="card flex items-center justify-center min-h-[180px]">
        <Spinner />
      </div>
    );
  }
  if (!deviceQ.data) return null;

  return (
    <ProductCard
      device={deviceQ.data}
      batteryHealthPct={ud.batteryHealthPct}
      purchasedAt={ud.purchasedAt}
      nickname={ud.nickname}
    />
  );
}

function AddProductTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="card flex flex-col items-center justify-center gap-2 min-h-[180px] text-neutral-400 hover:border-lime-500 hover:text-lime-400 transition-colors"
    >
      <span className="w-9 h-9 rounded-full bg-surface-700 flex items-center justify-center text-xl leading-none">
        +
      </span>
      <span className="text-sm font-medium">Add product</span>
    </button>
  );
}

function RankPanel() {
  const rankQ = useQuery({ queryKey: ['me', 'rank'], queryFn: getMyRank });

  if (rankQ.isLoading) return <Spinner />;
  if (rankQ.isError || !rankQ.data || rankQ.data.rank === null) {
    return (
      <p className="text-sm text-neutral-400">
        Track a device to see how your battery upkeep compares to other members.
      </p>
    );
  }

  const { rank, totalUsers, avgHealthPct } = rankQ.data;

  return (
    <div className="flex flex-col gap-3">
      <div className="text-lg">
        You are rank <span className="text-lime-400 font-semibold">{rank}</span> of {totalUsers}
      </div>
      <p className="text-sm text-neutral-400">
        Ranked by average battery health across all your tracked devices.
      </p>
      <div className="max-w-sm">
        <div className="flex justify-between text-xs text-neutral-400 mb-1">
          <span>Average battery health</span>
          <span>{avgHealthPct}%</span>
        </div>
        <div className="h-2 rounded-full bg-surface-600 overflow-hidden">
          <div className="h-full bg-lime-500" style={{ width: `${Math.max(0, Math.min(100, avgHealthPct ?? 0))}%` }} />
        </div>
      </div>
    </div>
  );
}

export function MyDevicesPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) || 'devices';
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [trackOpen, setTrackOpen] = useState(false);

  const myDevices = useMyDevices();

  const sorted = useMemo(() => {
    if (!myDevices.data) return [];
    const copy = [...myDevices.data];
    copy.sort((a, b) =>
      sortDir === 'asc' ? a.batteryHealthPct - b.batteryHealthPct : b.batteryHealthPct - a.batteryHealthPct
    );
    return copy;
  }, [myDevices.data, sortDir]);

  const needsAttention = useMemo(
    () =>
      (myDevices.data ?? []).filter(
        (d) => estimateBatteryLife(d.batteryHealthPct, d.purchasedAt).tone !== 'healthy'
      ),
    [myDevices.data]
  );

  const setTab = (next: Tab) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'devices') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-4">
        <div>
          <h1>My devices</h1>
          <p className="text-sm text-neutral-400">
            {user?.displayName ? `Hi ${user.displayName},` : 'Welcome,'} here&apos;s what needs your
            attention.
          </p>
        </div>
        <nav className="flex gap-6 border-b border-surface-600">
          {(
            [
              ['devices', 'My devices'],
              ['notifications', 'Notifications'],
              ['profile', 'Profile'],
            ] as [Tab, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`tab ${tab === key ? 'tab-active' : ''}`}
            >
              {label}
              {key === 'notifications' && needsAttention.length > 0 && (
                <span className="ml-1.5 text-accent-red">({needsAttention.length})</span>
              )}
            </button>
          ))}
        </nav>
      </header>

      {tab === 'devices' && (
        <>
          <div className="flex items-center justify-end">
            <label className="text-xs text-neutral-400 flex items-center gap-2">
              Sort by battery life
              <select
                value={sortDir}
                onChange={(e) => setSortDir(e.target.value as 'asc' | 'desc')}
                className="bg-surface-700 border border-surface-500 rounded-lg px-2 py-1 text-neutral-100"
              >
                <option value="asc">Most urgent first</option>
                <option value="desc">Healthiest first</option>
              </select>
            </label>
          </div>

          {myDevices.isLoading && <Spinner />}
          {myDevices.isError && <Card className="text-accent-red">Could not load your devices.</Card>}
          {myDevices.isSuccess && sorted.length === 0 && (
            <Card>
              <div className="text-neutral-300 mb-2">No devices tracked yet.</div>
              <Link to="/" className="text-lime-400 hover:underline text-sm">
                Browse the catalog →
              </Link>
            </Card>
          )}
          {myDevices.isSuccess && sorted.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sorted.map((ud) => (
                <li key={ud._id}>
                  <MyDeviceCard ud={ud} />
                </li>
              ))}
              <li>
                <AddProductTile onClick={() => setTrackOpen(true)} />
              </li>
            </ul>
          )}

          <Card className="mt-2">
            <h2 className="mb-2">Gamification</h2>
            <RankPanel />
          </Card>
        </>
      )}

      {tab === 'notifications' && (
        <div className="flex flex-col gap-3">
          {myDevices.isLoading && <Spinner />}
          {myDevices.isSuccess && needsAttention.length === 0 && (
            <Card className="text-neutral-400">All your batteries are healthy. Nothing to flag.</Card>
          )}
          {needsAttention.map((ud) => {
            const estimate = estimateBatteryLife(ud.batteryHealthPct, ud.purchasedAt);
            return (
              <Link
                key={ud._id}
                to={`/devices/${ud.deviceId}`}
                className="card flex items-center justify-between hover:border-lime-500 transition-colors"
              >
                <span>{ud.nickname || 'Tracked device'}</span>
                <StatusPill tone={estimate.tone} label={estimate.label} />
              </Link>
            );
          })}
        </div>
      )}

      {tab === 'profile' && (
        <Card className="flex flex-col gap-3 max-w-sm">
          <div>
            <div className="text-xs uppercase text-neutral-400">Name</div>
            <div className="text-lg font-semibold">{user?.displayName || '—'}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-neutral-400">Email</div>
            <div className="text-lg font-semibold">{user?.email}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-neutral-400">Role</div>
            <div className="text-lg font-semibold capitalize">{user?.role}</div>
          </div>
        </Card>
      )}

      <TrackDeviceModal open={trackOpen} onClose={() => setTrackOpen(false)} />
    </div>
  );
}
